/**
 * Documents Section Component
 * 
 * A reusable component for displaying legal documents and onboarding guides
 * with version history, e-signature support, and download tracking.
 * 
 * Used in: EnterprisePortal, PartnerPortal, MerchantPortal
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Download,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  History,
  Pen,
  Shield,
  BookOpen,
  ExternalLink,
  Loader2,
  Eye,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DocumentsSection = ({ 
  entityType, // 'partner', 'enterprise', 'merchant'
  entityId,
  entityName,
  entityEmail,
  entityTitle = "",
  accentColor = "purple" // purple for enterprise, orange for partner, blue for merchant
}) => {
  const [documents, setDocuments] = useState({ onboarding_guides: [], legal_agreements: [] });
  const [signedDocs, setSignedDocs] = useState([]);
  const [pendingDocs, setPendingDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signingDoc, setSigningDoc] = useState(null);
  const [signatureInput, setSignatureInput] = useState("");
  const [signing, setSigning] = useState(false);
  const [showVersionHistory, setShowVersionHistory] = useState(null);
  const [versionHistory, setVersionHistory] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  const colorClasses = {
    purple: {
      bg: "bg-purple-500/10",
      border: "border-purple-500/30",
      text: "text-purple-400",
      hover: "hover:bg-purple-500/20",
      button: "bg-purple-600 hover:bg-purple-700"
    },
    orange: {
      bg: "bg-viraly-orange/10",
      border: "border-viraly-orange/30",
      text: "text-viraly-orange",
      hover: "hover:bg-viraly-orange/20",
      button: "bg-viraly-orange hover:bg-viraly-orange/90"
    },
    blue: {
      bg: "bg-blue-500/10",
      border: "border-blue-500/30",
      text: "text-blue-400",
      hover: "hover:bg-blue-500/20",
      button: "bg-blue-600 hover:bg-blue-700"
    }
  };

  const colors = colorClasses[accentColor] || colorClasses.purple;

  useEffect(() => {
    fetchDocuments();
  }, [entityType, entityId]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const [docsRes, signedRes, pendingRes] = await Promise.all([
        apiClient.get(`/documents/for-entity/${entityType}`),
        apiClient.get(`/documents/signed/${entityType}/${entityId}`),
        apiClient.get(`/documents/pending/${entityType}/${entityId}`)
      ]);
      
      setDocuments(docsRes.data);
      setSignedDocs(signedRes.data.signed_documents || []);
      setPendingDocs(pendingRes.data.pending_documents || []);
    } catch (error) {
      console.error("Error fetching documents:", error);
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const fetchVersionHistory = async (documentId) => {
    setLoadingVersions(true);
    try {
      const res = await apiClient.get(`/documents/${documentId}/versions`);
      setVersionHistory(res.data.versions || []);
      setShowVersionHistory(documentId);
    } catch (error) {
      toast.error("Failed to load version history");
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      // Track the download
      await apiClient.post(`/documents/${doc.id}/track-download`, null, {
        params: { entity_type: entityType, entity_id: entityId }
      });
      
      // Open download in new tab
      const downloadUrl = `${window.location.origin}${doc.file_path}`;
      window.open(downloadUrl, '_blank');
      
      toast.success(`Downloading ${doc.title}`);
    } catch (error) {
      // Still allow download even if tracking fails
      const downloadUrl = `${window.location.origin}${doc.file_path}`;
      window.open(downloadUrl, '_blank');
    }
  };

  const handleSign = async () => {
    if (!signatureInput.trim()) {
      toast.error("Please enter your full legal name");
      return;
    }
    
    setSigning(true);
    try {
      const res = await apiClient.post('/documents/sign', {
        entity_type: entityType,
        entity_id: entityId,
        document_id: signingDoc.id,
        signatory_name: signatureInput,
        signatory_email: entityEmail,
        signatory_title: entityTitle
      });
      
      toast.success(`Document signed successfully! Signature ID: ${res.data.signature_hash}`);
      setShowSignModal(false);
      setSigningDoc(null);
      setSignatureInput("");
      
      // Refresh documents
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sign document");
    } finally {
      setSigning(false);
    }
  };

  const isDocSigned = (docId) => {
    return signedDocs.some(s => s.document_id === docId);
  };

  const getSignatureInfo = (docId) => {
    return signedDocs.find(s => s.document_id === docId);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const DocumentCard = ({ doc, isAgreement = false }) => {
    const signed = isDocSigned(doc.id);
    const sigInfo = getSignatureInfo(doc.id);
    const isExpanded = expandedDoc === doc.id;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className={`rounded-xl border ${colors.border} ${colors.bg} overflow-hidden`}
      >
        {/* Header */}
        <div 
          className="p-4 cursor-pointer"
          onClick={() => setExpandedDoc(isExpanded ? null : doc.id)}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${colors.bg}`}>
                {isAgreement ? (
                  <Shield className={`w-5 h-5 ${colors.text}`} />
                ) : (
                  <BookOpen className={`w-5 h-5 ${colors.text}`} />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-viraly-light">{doc.title}</h3>
                <p className="text-sm text-viraly-muted mt-0.5">{doc.category_name}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-viraly-muted">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    v{doc.version}
                  </span>
                  <span>|</span>
                  <span>{doc.jurisdiction_name}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAgreement && (
                signed ? (
                  <span className="flex items-center gap-1 text-xs text-green-400 bg-green-500/10 px-2 py-1 rounded-full">
                    <CheckCircle className="w-3 h-3" />
                    Signed
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded-full">
                    <AlertCircle className="w-3 h-3" />
                    Pending
                  </span>
                )
              )}
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-viraly-muted" />
              ) : (
                <ChevronDown className="w-5 h-5 text-viraly-muted" />
              )}
            </div>
          </div>
        </div>

        {/* Expanded Content */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="border-t border-white/5"
            >
              <div className="p-4 space-y-4">
                {/* Description */}
                {doc.description && (
                  <p className="text-sm text-viraly-muted">{doc.description}</p>
                )}

                {/* Signature Info (if signed) */}
                {signed && sigInfo && (
                  <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-sm font-medium text-green-400">Signed</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs text-viraly-muted">
                      <div>
                        <span className="text-viraly-light">By:</span> {sigInfo.signatory_name}
                      </div>
                      <div>
                        <span className="text-viraly-light">Date:</span> {formatDate(sigInfo.signed_at)}
                      </div>
                      <div className="col-span-2">
                        <span className="text-viraly-light">Signature ID:</span> {sigInfo.signature_hash}
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(doc);
                    }}
                    className="text-xs"
                    data-testid={`download-${doc.category}`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Download
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      fetchVersionHistory(doc.id);
                    }}
                    className="text-xs"
                    data-testid={`versions-${doc.category}`}
                  >
                    <History className="w-3 h-3 mr-1" />
                    Version History
                  </Button>

                  {isAgreement && !signed && (
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSigningDoc(doc);
                        setShowSignModal(true);
                      }}
                      className={`text-xs ${colors.button}`}
                      data-testid={`sign-${doc.category}`}
                    >
                      <Pen className="w-3 h-3 mr-1" />
                      Sign Document
                    </Button>
                  )}

                  <a
                    href={doc.file_path}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-viraly-muted hover:text-viraly-light flex items-center gap-1"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open in new tab
                  </a>
                </div>

                {/* File Info */}
                <div className="text-xs text-viraly-muted pt-2 border-t border-white/5">
                  <span>File: {doc.original_filename || doc.filename}</span>
                  {doc.file_size && (
                    <span className="ml-4">Size: {(doc.file_size / 1024).toFixed(1)} KB</span>
                  )}
                  {doc.download_count > 0 && (
                    <span className="ml-4">Downloads: {doc.download_count}</span>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={`w-8 h-8 ${colors.text} animate-spin`} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="documents-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-viraly-light">Documents</h2>
          <p className="text-viraly-muted text-sm mt-1">
            Legal agreements and onboarding guides for your account
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchDocuments}
          className="text-xs"
        >
          <History className="w-3 h-3 mr-1" />
          Refresh
        </Button>
      </div>

      {/* Pending Documents Alert */}
      {pendingDocs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-400">
                {pendingDocs.length} Document{pendingDocs.length > 1 ? 's' : ''} Pending Signature
              </h3>
              <p className="text-sm text-yellow-200/70 mt-1">
                Please review and sign the following documents to complete your onboarding:
              </p>
              <ul className="mt-2 space-y-1">
                {pendingDocs.map(doc => (
                  <li key={doc.id} className="text-sm text-yellow-200/70">
                    • {doc.title}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.div>
      )}

      {/* Onboarding Guides */}
      {documents.onboarding_guides.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <BookOpen className={`w-5 h-5 ${colors.text}`} />
            Onboarding Guides
          </h3>
          <div className="space-y-3">
            {documents.onboarding_guides.map(doc => (
              <DocumentCard key={doc.id} doc={doc} />
            ))}
          </div>
        </div>
      )}

      {/* Legal Agreements */}
      {documents.legal_agreements.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-viraly-light mb-4 flex items-center gap-2">
            <Shield className={`w-5 h-5 ${colors.text}`} />
            Legal Agreements
          </h3>
          <div className="space-y-3">
            {documents.legal_agreements.map(doc => (
              <DocumentCard key={doc.id} doc={doc} isAgreement />
            ))}
          </div>
        </div>
      )}

      {/* No Documents */}
      {documents.onboarding_guides.length === 0 && documents.legal_agreements.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-viraly-muted mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-viraly-light">No Documents Available</h3>
          <p className="text-viraly-muted text-sm mt-1">
            Documents will appear here when they are assigned to your account.
          </p>
        </div>
      )}

      {/* Version History Modal */}
      <Dialog open={!!showVersionHistory} onOpenChange={() => setShowVersionHistory(null)}>
        <DialogContent className="bg-viraly-dark border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-viraly-light flex items-center gap-2">
              <History className={`w-5 h-5 ${colors.text}`} />
              Version History
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {loadingVersions ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-viraly-muted" />
              </div>
            ) : versionHistory.length === 0 ? (
              <p className="text-viraly-muted text-sm text-center py-4">No version history available</p>
            ) : (
              versionHistory.map((v, i) => (
                <div 
                  key={v.id}
                  className={`p-3 rounded-lg border ${i === 0 ? `${colors.border} ${colors.bg}` : 'border-white/5 bg-white/5'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium ${i === 0 ? colors.text : 'text-viraly-light'}`}>
                      Version {v.version}
                    </span>
                    {i === 0 && (
                      <span className={`text-xs px-2 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                        Current
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-viraly-muted">{v.changes}</p>
                  <p className="text-xs text-viraly-muted mt-1">
                    {formatDate(v.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* E-Signature Modal */}
      <Dialog open={showSignModal} onOpenChange={setShowSignModal}>
        <DialogContent className="bg-viraly-dark border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-viraly-light flex items-center gap-2">
              <Pen className={`w-5 h-5 ${colors.text}`} />
              Sign Document
            </DialogTitle>
          </DialogHeader>
          {signingDoc && (
            <div className="space-y-4">
              {/* Document Info */}
              <div className={`p-3 rounded-lg ${colors.bg} border ${colors.border}`}>
                <h4 className="font-medium text-viraly-light">{signingDoc.title}</h4>
                <p className="text-sm text-viraly-muted mt-1">{signingDoc.description}</p>
                <p className="text-xs text-viraly-muted mt-2">
                  Version {signingDoc.version} • {signingDoc.jurisdiction_name}
                </p>
              </div>

              {/* Legal Notice */}
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-sm">
                <p className="text-yellow-200/80">
                  <strong className="text-yellow-400">Legal Notice:</strong> By typing your name below, 
                  you acknowledge that this constitutes your electronic signature and you agree to be 
                  bound by the terms of this agreement. This signature is legally binding under the 
                  Electronic Transactions Act (Canada) and equivalent international e-signature laws.
                </p>
              </div>

              {/* Signature Input */}
              <div>
                <label className="text-sm text-viraly-muted mb-2 block">
                  Type your full legal name to sign:
                </label>
                <Input
                  value={signatureInput}
                  onChange={(e) => setSignatureInput(e.target.value)}
                  placeholder={entityName || "Your full legal name"}
                  className="bg-white/5 border-white/10 text-viraly-light"
                  data-testid="signature-input"
                />
                <p className="text-xs text-viraly-muted mt-1">
                  Signing as: {entityEmail}
                </p>
              </div>

              {/* Preview Document Link */}
              <a
                href={signingDoc.file_path}
                target="_blank"
                rel="noopener noreferrer"
                className={`text-sm ${colors.text} hover:underline flex items-center gap-1`}
              >
                <Eye className="w-4 h-4" />
                Preview document before signing
              </a>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSignModal(false);
                    setSigningDoc(null);
                    setSignatureInput("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSign}
                  disabled={signing || !signatureInput.trim()}
                  className={colors.button}
                  data-testid="confirm-signature-btn"
                >
                  {signing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing...
                    </>
                  ) : (
                    <>
                      <Pen className="w-4 h-4 mr-2" />
                      Sign Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsSection;
